import { useState, useEffect, useRef } from 'react';
import { mapAPI } from '../lib/api';
import { Loader2, MapPin, Car, Route, Users, Clock, DollarSign, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { devError } from '../lib/devLog';
import { setSafeHTML } from '../lib/safeHtml';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const InteractiveMap = ({ height = '400px', showJobs = true, showRoutes = true }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    fetchMapData();
    return () => {
      // Cleanup map on unmount
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapData || !mapContainer.current || map.current) return;

    // Dynamically load Mapbox GL JS
    const loadMapbox = async () => {
      try {
        if (!window.mapboxgl) {
          const script = document.createElement('script');
          script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
          script.async = true;
          
          const link = document.createElement('link');
          link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
          link.rel = 'stylesheet';
          
          document.head.appendChild(link);
          
          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        initializeMap();
      } catch (err) {
        devError('Failed to load Mapbox:', err);
        setError('Failed to load map');
      }
    };

    loadMapbox();
  }, [mapData]);

  const fetchMapData = async () => {
    try {
      const response = await mapAPI.getData();
      setMapData(response.data);
    } catch (err) {
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const initializeMap = () => {
    try {
      if (!window.mapboxgl || !mapData || !mapContainer.current) return;

      window.mapboxgl.accessToken = MAPBOX_TOKEN;

      map.current = new window.mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [mapData.hub.lng, mapData.hub.lat],
        zoom: 10,
      });

      map.current.on('load', () => {
        setMapLoaded(true);
        addHubMarker();
        addZoneCircles();
        if (showJobs) addJobMarkers();
        if (showRoutes) addRouteMarkers();
        addDriverLocations();
      });

      map.current.on('error', (e) => {
        devError('Map error:', e);
      });

      // Add navigation controls
      map.current.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
    } catch (err) {
      devError('Failed to initialize map:', err);
      setError('Failed to initialize map');
    }
  };

  const addHubMarker = () => {
    if (!map.current || !mapData) return;

    const hubs = (mapData.hubs && mapData.hubs.length > 0)
      ? mapData.hubs
      : [{ name: mapData.hub.name, lat: mapData.hub.lat, lng: mapData.hub.lng, state: mapData.hub.state, is_primary: true }];

    hubs.forEach(hub => {
      const isPrimary = !!hub.is_primary;
      const hubEl = document.createElement('div');
      hubEl.className = 'hub-marker';
      setSafeHTML(hubEl, `
        <div style="
          width: ${isPrimary ? 40 : 32}px;
          height: ${isPrimary ? 40 : 32}px;
          background: ${isPrimary ? '#F97316' : '#8B5CF6'};
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px ${isPrimary ? 'rgba(249, 115, 22, 0.4)' : 'rgba(139, 92, 246, 0.4)'};
          border: 3px solid white;
        ">
          <svg width="${isPrimary ? 20 : 16}" height="${isPrimary ? 20 : 16}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
            <polyline points="9,22 9,12 15,12 15,22"/>
          </svg>
        </div>
      `);

      const popup = new window.mapboxgl.Popup();
      const popupNode = document.createElement('div');
      popupNode.style.padding = '8px';
      const title = document.createElement('strong');
      title.textContent = `${isPrimary ? 'Primary Hub' : 'Hub'}: ${hub.name}, ${hub.state || 'IL'}`;
      const desc = document.createElement('p');
      desc.style.cssText = 'margin: 4px 0 0; color: #666; font-size: 12px;';
      desc.textContent = 'Service center';
      popupNode.appendChild(title);
      popupNode.appendChild(desc);
      popup.setDOMContent(popupNode);

      new window.mapboxgl.Marker(hubEl)
        .setLngLat([hub.lng, hub.lat])
        .setPopup(popup)
        .addTo(map.current);
    });
  };

  const addZoneCircles = () => {
    if (!map.current || !mapData) return;

    const colors = ['#22c55e', '#3b82f6', '#a855f7', '#f97316', '#ef4444', '#64748b'];

    const hubs = (mapData.hubs && mapData.hubs.length > 0)
      ? mapData.hubs
      : [{ lat: mapData.hub.lat, lng: mapData.hub.lng, zones: mapData.zones, is_primary: true }];

    hubs.forEach((hub, hubIdx) => {
      const zones = hub.zones && hub.zones.length ? hub.zones : mapData.zones;
      const isPrimary = !!hub.is_primary;
      zones.forEach((zone, index) => {
        const sourceId = `zone-h${hubIdx}-z${index}`;
        if (map.current.getSource(sourceId)) return;

        const radiusInMeters = (zone.radius_miles || zone.max_distance || 0) * 1609.34;
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: createCircleGeoJSON([hub.lng, hub.lat], radiusInMeters),
        });

        map.current.addLayer({
          id: `zone-fill-h${hubIdx}-z${index}`,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': colors[index % colors.length],
            'fill-opacity': isPrimary ? 0.08 : 0.04,
          },
        });

        map.current.addLayer({
          id: `zone-border-h${hubIdx}-z${index}`,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': colors[index % colors.length],
            'line-width': isPrimary ? 2 : 1,
            'line-opacity': isPrimary ? 0.5 : 0.3,
            'line-dasharray': isPrimary ? [1] : [2, 2],
          },
        });
      });
    });
  };

  const createCircleGeoJSON = (center, radiusInMeters) => {
    const points = 64;
    const coords = [];
    
    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;
      const dx = radiusInMeters * Math.cos(angle);
      const dy = radiusInMeters * Math.sin(angle);
      
      const lat = center[1] + (dy / 111320);
      const lng = center[0] + (dx / (111320 * Math.cos(center[1] * Math.PI / 180)));
      
      coords.push([lng, lat]);
    }
    coords.push(coords[0]); // Close the polygon

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coords],
      },
    };
  };

  const addJobMarkers = () => {
    if (!map.current || !mapData?.jobs) return;

    mapData.jobs.forEach((job) => {
      const statusColors = {
        pending: '#eab308',
        accepted: '#3b82f6',
        in_progress: '#a855f7',
      };

      const el = document.createElement('div');
      el.className = 'job-marker';
      el.style.cssText = `
        width: 32px; 
        height: 32px; 
        background: ${statusColors[job.status] || '#6b7280'}; 
        border-radius: 50%; 
        display: flex; 
        align-items: center; 
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        border: 2px solid white;
      `;
      setSafeHTML(el, job.job_type === 'ride' 
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10l-2.4-3.2c-.3-.4-.8-.6-1.3-.6H9.7c-.5 0-1 .2-1.3.6L6 10l-2.5 1.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M16 16h6"/><path d="M2 11h6l3-3 6 6 4-4"/><path d="M5.5 6.5L12 12l2-2"/></svg>');
      
      el.onclick = () => setSelectedItem({ type: 'job', data: job });

      // Use hub coordinates as fallback (jobs don't have geocoded coordinates yet)
      new window.mapboxgl.Marker(el)
        .setLngLat([mapData.hub.lng + (Math.random() - 0.5) * 0.1, mapData.hub.lat + (Math.random() - 0.5) * 0.1])
        .addTo(map.current);
    });
  };

  const addRouteMarkers = () => {
    if (!map.current || !mapData?.routes) return;

    mapData.routes.forEach((route) => {
      const el = document.createElement('div');
      el.className = 'route-marker';
      el.style.cssText = `
        width: 32px; 
        height: 32px; 
        background: #10b981; 
        border-radius: 8px; 
        display: flex; 
        align-items: center; 
        justify-content: center;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,0.2);
        border: 2px solid white;
      `;
      setSafeHTML(el, '<svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></svg>');
      el.onclick = () => setSelectedItem({ type: 'route', data: route });

      new window.mapboxgl.Marker(el)
        .setLngLat([mapData.hub.lng + (Math.random() - 0.5) * 0.08, mapData.hub.lat + (Math.random() - 0.5) * 0.08])
        .addTo(map.current);
    });
  };

  const addDriverLocations = () => {
    if (!map.current || !mapData?.driver_locations) return;

    mapData.driver_locations.forEach((loc) => {
      const el = document.createElement('div');
      el.className = 'driver-marker';
      el.style.cssText = `
        width: 28px; 
        height: 28px; 
        background: #3b82f6; 
        border-radius: 50%; 
        display: flex; 
        align-items: center; 
        justify-content: center;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
        border: 2px solid white;
        animation: pulse 2s infinite;
      `;
      setSafeHTML(el, '<svg width="14" height="14" viewBox="0 0 24 24" fill="white"><circle cx="12" cy="12" r="10"/></svg>');

      new window.mapboxgl.Marker(el)
        .setLngLat([loc.longitude, loc.latitude])
        .setPopup(new window.mapboxgl.Popup().setText(loc.driver_name || 'Active driver'))
        .addTo(map.current);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-zinc-100 rounded-xl" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center bg-zinc-100 rounded-xl" style={{ height }}>
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden border border-zinc-200" style={{ height }}>
      <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 text-xs">
        <p className="font-semibold text-zinc-800 mb-2">Legend</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>Hub Center</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <span>Pending Job</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Active Driver</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>Driver Route</span>
          </div>
        </div>
      </div>

      {/* Zone info */}
      {mapData?.zones && (
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 text-xs max-w-[200px]">
          <p className="font-semibold text-zinc-800 mb-2">Service Zones</p>
          <div className="space-y-1">
            {mapData.zones.map((zone) => (
              <div key={zone.name} className="flex justify-between">
                <span>{zone.name}</span>
                <span className="text-zinc-500">{zone.radius_miles}mi (+${zone.addon})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected item popup */}
      {selectedItem && (
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-[280px]">
          <button 
            onClick={() => setSelectedItem(null)}
            className="absolute top-2 right-2 text-zinc-400 hover:text-zinc-600"
          >
            ×
          </button>
          
          {selectedItem.type === 'job' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Car className="w-5 h-5 text-orange-500" />
                <span className="font-semibold capitalize">
                  {selectedItem.data.job_type === 'ride' ? 'Ride' : 'Delivery'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedItem.data.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  selectedItem.data.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {selectedItem.data.status}
                </span>
              </div>
              <p className="text-xs text-zinc-600 mb-1">
                <strong>From:</strong> {selectedItem.data.pickup}
              </p>
              <p className="text-xs text-zinc-600 mb-2">
                <strong>To:</strong> {selectedItem.data.dropoff}
              </p>
              <p className="text-sm font-semibold text-green-600">
                ${selectedItem.data.price?.toFixed(2)}
              </p>
            </div>
          )}
          
          {selectedItem.type === 'route' && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Route className="w-5 h-5 text-green-500" />
                <span className="font-semibold">{selectedItem.data.route_name}</span>
              </div>
              <p className="text-xs text-zinc-600 mb-1">
                <strong>Driver:</strong> {selectedItem.data.driver_name}
              </p>
              <p className="text-xs text-zinc-600 mb-1">
                <strong>From:</strong> {selectedItem.data.origin}
              </p>
              <p className="text-xs text-zinc-600 mb-1">
                <strong>To:</strong> {selectedItem.data.destination}
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {selectedItem.data.departure_time}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {selectedItem.data.available_seats} seats
                </span>
              </div>
              <p className="text-sm font-semibold text-green-600 mt-2">
                ${selectedItem.data.price_per_seat?.toFixed(2)}/seat
              </p>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default InteractiveMap;
